if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const thumbnailButton = mediaToSwitch.querySelector('button');
          // Sync the variant picker FIRST: if this fires a color change, Dawn's
          // own onVariantChange -> updateMedia will jump the viewer to that
          // color's official featured photo. We then run setActiveMedia SECOND
          // so the photo the shopper actually clicked wins and stays on screen.
          thumbnailButton.addEventListener('click', this.syncVariantFromMedia.bind(this, mediaToSwitch));
          thumbnailButton.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      syncVariantFromMedia(mediaToSwitch) {
        const dataTarget = mediaToSwitch.dataset.target;
        const colorAttr = mediaToSwitch.dataset.color;
        const sectionId = this.id.replace('MediaGallery-', '').replace('-duplicate', '');
        const variantSelects = document.querySelector(
          `variant-selects[data-section="${sectionId}"], variant-radios[data-section="${sectionId}"]`
        );
        if (!variantSelects) return;

        let colorValue = null;

        if (colorAttr) {
          // Preferred: an explicit color tag set on the photo itself (via alt
          // text starting with "Color: …"). This lets ANY number of photos —
          // not just each variant's single official featured image — be
          // linked to a color, so clicking any of them selects the right color.
          colorValue = colorAttr;
        } else {
          // Fallback: match against each variant's single featured_media id
          // (the only linkage Shopify guarantees for every variant).
          const mediaId = dataTarget.split('-').pop();
          let variantData;
          try {
            const script = variantSelects.querySelector('[type="application/json"]');
            if (!script) return;
            variantData = JSON.parse(script.textContent);
          } catch (e) {
            return;
          }
          // NOTE: variant.featured_image (legacy Image object) and
          // variant.featured_media (unified Media object) use different id
          // spaces. The gallery's data-target ids come from product.media, so
          // we must match against featured_media.id here, not featured_image.id.
          const matchingVariant = variantData.find(
            (variant) => variant.featured_media && String(variant.featured_media.id) === mediaId
          );
          if (!matchingVariant || !matchingVariant.option1) return;
          colorValue = matchingVariant.option1;
        }

        if (!colorValue) return;

        const selects = variantSelects.querySelectorAll('select');
        if (selects.length) {
          const colorSelect = selects[0];
          if (colorSelect.value === colorValue) return;
          const hasOption = Array.from(colorSelect.options).some((option) => option.value === colorValue);
          if (!hasOption) return;
          colorSelect.value = colorValue;
          colorSelect.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const fieldsets = variantSelects.querySelectorAll('fieldset');
        if (fieldsets.length) {
          const colorInput = Array.from(fieldsets[0].querySelectorAll('input[type="radio"]')).find(
            (radio) => radio.value === colorValue
          );
          if (colorInput && !colorInput.checked) {
            colorInput.checked = true;
            colorInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
